<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  images: { type: Array, required: true }, // [{src, alt}]
  aspect: { type: String, default: '16/9' } // desktop aspect-ratio
});

const idx = ref(0);
const max = computed(() => props.images.length);
const next = () => { idx.value = (idx.value + 1) % max.value; };
const prev = () => { idx.value = (idx.value - 1 + max.value) % max.value; };

// Show a soft placeholder instead of a broken-image icon until real
// photos are dropped into public/images/ with the filenames used above.
const failed = ref(false);
watch(idx, () => { failed.value = false; });
</script>

<template>
  <div class="carousel card" :style="{ '--car-aspect': aspect }">
    <div class="viewport">
      <img
          v-show="!failed"
          :src="images[idx].src"
          :alt="images[idx].alt || ''"
          @error="failed = true"
      />
      <div v-if="failed" class="placeholder" aria-hidden="true">
        <svg width="40" height="40" viewBox="0 0 24 24">
          <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" stroke-width="1.4"/>
          <circle cx="9" cy="10" r="1.6" fill="currentColor"/>
          <path d="M4 17l5-5 4 4 3-3 4 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>{{ images[idx].alt || 'Foto volgt' }}</span>
      </div>
    </div>

    <button class="nav left" aria-label="Vorige" @click="prev">
      <svg viewBox="0 0 24 24"><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2"/></svg>
    </button>
    <button class="nav right" aria-label="Volgende" @click="next">
      <svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2"/></svg>
    </button>

    <div class="dots" role="tablist" aria-label="Afbeelding selectie">
      <button
          v-for="(img,i) in images"
          :key="i"
          class="dot"
          :aria-selected="i===idx"
          @click="idx=i"
          :title="img.alt || ('Afbeelding ' + (i+1))"
      />
    </div>
  </div>
</template>

<style scoped>
.carousel {
  position: relative;
  overflow: hidden;
}

.viewport {
  aspect-ratio: var(--car-aspect, 16 / 9);
  display: grid;
  place-items: center;
  background: var(--bg-soft);
  border-radius: var(--radius-lg);
}
/* On phones a wide letterbox becomes a sliver — use a comfortable ratio. */
@media (max-width: 700px) {
  .viewport { aspect-ratio: 4 / 3; }
}

.viewport img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--radius-lg);
}

.placeholder {
  width: 100%; height: 100%; border-radius: var(--radius-lg);
  display: grid; place-items: center; gap: 8px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--brand) 16%, #fff), color-mix(in srgb, var(--accent) 16%, #fff));
  color: var(--muted);
  text-align: center;
  padding: 16px;
}
.placeholder span { font-size: 13px; font-weight: 600; }

.nav {
  position: absolute; top: 50%; transform: translateY(-50%);
  background: rgba(255,255,255,.85);
  border: 1px solid var(--divider);
  width: 38px; height: 38px; border-radius: 999px;
  display: grid; place-items: center; cursor: pointer; box-shadow: var(--shadow-sm);
}
.nav.left { left: 10px; }
.nav.right { right: 10px; }
.nav:hover { background: #fff; box-shadow: var(--shadow-md); }

.dots {
  position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%);
  display: flex; gap: 8px;
}
.dot {
  width: 8px; height: 8px; border-radius: 999px;
  border: 1px solid var(--divider);
  background: #fff;
  opacity: .6;
}
.dot[aria-selected="true"] {
  background: var(--brand);
  border-color: var(--brand);
  opacity: 1;
}
</style>
